import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import routes from '../../../routesConfig';
import Forms from '../../../components/Forms';
import {useModal} from "../../../contexts/ModalContext";
import SaleModal from "../../../components/SaleModal";
import RepairIconButton from "../../../components/Forms/RepairIconButton";
import SellIconButton from "../../../components/Forms/SellIconButton";
import EditIconButton from "../../../components/Forms/EditIconButton";
import DeleteIconButton from "../../../components/Forms/DeleteIconButton";
import DonorDiagnosticIconButton from "../../../components/Forms/DonorDiagnosticIconButton";
import AdminDataView from "../../../components/AdminDataView";
import BuybackTiles from "./BuybackTiles";
import {getBadgeStatusClass, getRowStatusClass} from "../../../utils/adminStatus";
import {resolveBuybackPhotoUrl} from "../../../utils/adminMedia";
import PhotoThumbWithHover from "../../../components/PhotoThumbWithHover";
import DonorDiagnosticModal from "../../../components/DonorDiagnosticModal";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";

const STATUS_OPTIONS = [
    {value: null, label: 'Vše'},
    {value: 'intake', label: 'Přijato'},
    {value: 'repairing', label: 'V opravě'},
    {value: 'repaired', label: 'Opraveno'},
    {value: 'listed', label: 'Vystaveno'},
    {value: 'sold', label: 'Prodáno'}
];

const Buyback = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [buybacks, setBuybacks] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [filter, setFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [error, setError] = useState(null);
    const [buybackCurrency, setBuybackCurrency] = useState('CZK');
    const filterInputRef = useRef(null);
    const {openModal, confirm} = useModal();

    const fetchBuybacks = useCallback(async () => {
        const res = await API.get(API_ENDPOINTS.buybacks.list);
        const rows = (res.data || []).map((bb) => ({...bb, key: bb._id || bb.id}));
        setBuybacks(rows);
        setFiltered(rows);
    }, []);

    const fetchAppSettings = useCallback(async () => {
        const res = await API.get(API_ENDPOINTS.appSettings.current);
        const currency = res?.data?.buybackCurrency;
        if (currency) {
            setBuybackCurrency(currency);
        }
    }, []);

    useEffect(() => {
        Promise.all([fetchBuybacks(), fetchAppSettings()]).catch(console.error);
    }, [fetchAppSettings, fetchBuybacks]);

    useEffect(() => {
        const q = (filter || '').toLowerCase().trim();
        const rows = buybacks.filter((row) => {
            const customer = row.source?.customerId;
            const customerLabel = getCustomerLabel(customer).toLowerCase();
            const identifiers = getIdentifierValues(row.item?.identifiers).join(' ').toLowerCase();
            const deviceLabel = `${row.deviceSnapshot?.name || row.device?.name || ''} ${row.deviceSnapshot?.code || row.device?.code || ''}`.toLowerCase();
            const sourceName = `${row.source?.sellerName || ''} ${row.source?.sellerContact || ''}`.toLowerCase();

            const haystack = [
                row.buybackNumber,
                deviceLabel,
                identifiers,
                customerLabel,
                sourceName
            ].filter(Boolean).join(' ').toLowerCase();

            const statusOk = statusFilter ? row.status === statusFilter : true;
            return haystack.includes(q) && statusOk;
        });
        setFiltered(rows);
    }, [buybacks, filter, statusFilter]);

    useEffect(() => {
        const onKey = (e) => {
            if (!e.shiftKey && filterInputRef.current && document.activeElement !== filterInputRef.current) {
                filterInputRef.current.focus();
            }
            if (e.shiftKey && e.key === 'N') {
                e.preventDefault();
                navigate(routes.admin.buyback.addFull);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [navigate]);

    const askDelete = useCallback(async (row) => {
        const result = await confirm(`Opravdu chcete smazat výkup ${row?.buybackNumber || ''}?`);
        if (result !== true) return;
        try {
            await API.delete(API_ENDPOINTS.buybacks.delete(row._id));
            setBuybacks(prev => prev.filter(item => item._id !== row._id));
            setError(null);
        } catch (e) {
            console.error(e);
            setError(e);
        }
    }, [confirm]);

    const handlePrepareForSale = useCallback(async (row) => {
        (async () => {
            try {
                const res = await API.get(API_ENDPOINTS.buybacks.detail(row._id));
                openModal(SaleModal, {
                    initialBuyback: res?.data || row,
                    onSaved: fetchBuybacks
                }, {title: `Příprava prodeje ${row.buybackNumber || ''}`});
            } catch (e) {
                console.error(e);
                setError(e);
            }
        })();
    }, [fetchBuybacks, openModal]);

    const openDonorDiagnostic = useCallback(async (row) => {
        try {
            const res = await API.get(API_ENDPOINTS.buybacks.detail(row._id));
            openModal(DonorDiagnosticModal, {
                buyback: res?.data || row,
                onSaved: fetchBuybacks
            }, {title: `Diagnostika donor dílů ${row.buybackNumber || ''}`});
        } catch (e) {
            console.error(e);
            setError(e);
        }
    }, [fetchBuybacks, openModal]);

    const columns = useMemo(() => ([
        {
            key: 'photo',
            name: 'Fotografie',
            renderCell: ({row}) => {
                const imageUrl = resolveBuybackImageUrl(row);
                return (
                    <div className="h-full w-full flex items-center justify-start">
                        <PhotoThumbWithHover src={imageUrl} alt={row.buybackNumber || 'Výkup'}/>
                    </div>
                );
            }
        },
        {
            key: 'buybackNumber',
            name: 'Číslo výkupu',
            renderCell: ({row}) => row.buybackNumber || '-'
        },
        {
            key: 'device',
            name: 'Zařízení',
            renderCell: ({row}) => {
                const name = row.deviceSnapshot?.name || row.device?.name || '-';
                const code = row.deviceSnapshot?.code || row.device?.code || '';
                const color = row.item?.color || '';
                const storage = row.item?.storage?.storage && row.item?.storage?.unit
                    ? `${row.item.storage.storage} ${String(row.item.storage.unit).toUpperCase()}`
                    : '';
                return (
                    <div className="leading-tight">
                        <div className="font-medium">{name} {code ? `(${code})` : ''}</div>
                        <div className="text-xs opacity-70">{[color, storage].filter(Boolean).join(' • ') || '-'}</div>
                    </div>
                );
            }
        },
        {
            key: 'source',
            name: 'Zdroj',
            renderCell: ({row}) => {
                if (row.source?.type === 'customer') {
                    return getCustomerLabel(row.source?.customerId) || '-';
                }
                if (row.source?.type === 'external_marketplace') {
                    const parts = [row.source?.marketplace, row.source?.sellerName].filter(Boolean);
                    return parts.join(' • ') || '-';
                }
                return 'Interní';
            }
        },
        {
            key: 'economics',
            name: 'Cena',
            renderCell: ({row}) => {
                const purchasePrice = row.economics?.purchasePrice;
                return formatMoney(purchasePrice, buybackCurrency);
            }
        },
        {
            key: 'status',
            name: t('status'),
            renderCell: ({row}) => <StatusBadge status={row.status}/>
        },
        {
            key: 'intakePurpose',
            name: 'Účel',
            renderCell: ({row}) => (
                row.intakePurpose === 'donor'
                    ? <PrecisionManufacturingOutlinedIcon fontSize="small" titleAccess="Donor"/>
                    : <BuildOutlinedIcon fontSize="small" titleAccess="K opravě"/>
            )
        },
        {
            key: 'repair',
            name: 'Oprava',
            renderCell: ({row}) => {
                const repair = getLinkedRepair(row);
                if (!repair) return <span className="text-xs opacity-60">Bez opravy</span>;
                const repairId = repair?._id ? String(repair._id) : null;

                return (
                    <div className="leading-tight">
                        <div className="font-medium">
                            {repairId ? (
                                <button
                                    type="button"
                                    onClick={() => navigate(routes.admin.getEditRepair(repairId))}
                                    className="text-blue-700 hover:underline"
                                >
                                    {repair.repairNumber ? `#${repair.repairNumber}` : 'Detail opravy'}
                                </button>
                            ) : (
                                <span>{repair.repairNumber ? `#${repair.repairNumber}` : '-'}</span>
                            )}
                        </div>
                        <div className="text-xs opacity-70">{getRepairStatusLabel(repair.status, t)}</div>
                    </div>
                );
            }
        },
        {
            key: 'sale',
            name: 'Prodej',
            renderCell: ({row}) => {
                const sale = getLinkedSale(row);
                if (!sale) return <span className="text-xs opacity-60">Bez prodeje</span>;
                const saleId = sale?._id ? String(sale._id) : null;

                return (
                    <div className="leading-tight">
                        <div className="font-medium">
                            {saleId ? (
                                <button
                                    type="button"
                                    onClick={() => navigate(routes.admin.getEditSale(saleId))}
                                    className="text-blue-700 hover:underline"
                                >
                                    {sale.saleNumber ? `#${sale.saleNumber}` : 'Detail prodeje'}
                                </button>
                            ) : (
                                <span>{sale.saleNumber ? `#${sale.saleNumber}` : '-'}</span>
                            )}
                        </div>
                        <div className="text-xs opacity-70">{sale.status === 'sold' ? 'Prodáno' : 'Vystaveno'}</div>
                    </div>
                );
            }
        },
        {
            key: 'dealAt',
            name: t('date'),
            renderCell: ({row}) => {
                const date = row.dealAt || row.createdAt;
                return date ? moment(date).format('DD.MM.YYYY') : '-';
            }
        },
        {
            key: 'actions',
            name: 'Akce',
            renderCell: ({row}) => {
                const hasRepair = Boolean(getLinkedRepair(row));
                const isDonor = row.intakePurpose === 'donor';
                const canCreateRepair = !hasRepair && !isDonor;
                const repair = getLinkedRepair(row);
                const sale = getLinkedSale(row);
                const repairCompleted = repair?.status === 'completed';
                const canPrepareForSale = !isDonor && repairCompleted && !sale;
                const donorLocked = Boolean(row?.item?.donorPartsLockedAt);
                return (
                    <div className="flex justify-end items-center h-full w-full w-full">
                        {canCreateRepair ?
                            <RepairIconButton
                                title="Založit opravu"
                                onClick={() => navigate(`${routes.admin.repairsFull}?buybackId=${row._id}`)}
                            /> : null}

                        {canPrepareForSale ? (
                            <SellIconButton
                                title="Zalistovat do prodeje"
                                onClick={() => handlePrepareForSale(row)}
                            />) : null}
                        {isDonor ? (
                            <DonorDiagnosticIconButton
                                title={donorLocked ? "Diagnostika donor dílů (naskladněno)" : "Diagnostika donor dílů"}
                                onClick={() => openDonorDiagnostic(row)}
                            />
                        ) : null}

                        <EditIconButton
                            title="Upravit výkup"
                            onClick={() => navigate(routes.admin.getEditBuyback(row._id))}

                        />

                        <DeleteIconButton
                            title="Smazat výkup"
                            onClick={() => askDelete(row)}
                        />
                    </div>
                );
            }
        }
    ]).map(column => ({...column, resizable: true})), [askDelete, buybackCurrency, handlePrepareForSale, navigate, openDonorDiagnostic, t]);

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
            <Forms.Button label="Přidat výkup" onClick={() => navigate(routes.admin.buyback.addFull)}/>
                <div className="grid grid-cols-3 gap-4 items-end">
                    <Forms.Text
                        // ref={filterRef}
                        value={filter}
                        onChange={setFilter}
                        label={t('search')}
                        placeholder="Číslo, zařízení, IMEI/SN, zákazník"
                    />
                    <Forms.Select
                        label={t('status')}
                        value={STATUS_OPTIONS.find(option => option.value === statusFilter) || STATUS_OPTIONS[0]}
                        onChange={option => setStatusFilter(option?.value || null)}
                        options={STATUS_OPTIONS}
                    />
                    <Forms.Button
                        className="bg-gray-600 hover:bg-gray-700"
                        label={t('reset')}
                        onClick={() => {
                            setFilter('');
                            setStatusFilter(null);
                            Promise.all([fetchBuybacks(), fetchAppSettings()]).catch(console.error);
                        }}
                    />
                </div>
            </div>
            {error ? <div className="text-sm text-red-600 mb-3">{error?.message || 'Nastala chyba.'}</div> : null}

            <AdminDataView
                section="buybacks"
                rows={filtered}
                columns={columns}
                resolvePhotoUrl={resolveBuybackPhotoUrl}
                dataGridProps={{rowClassRenderer: (row) => getRowStatusClass(row?.status)}}
                renderTile={({row, visibleFields, actionColumn}) => (
                    <BuybackTiles
                        row={row}
                        visibleFields={visibleFields}
                        actionColumn={actionColumn}
                        buybackCurrency={buybackCurrency}
                    />
                )}
            />
        </div>
    );
};

const StatusBadge = ({status}) => {
    const labelMap = {
        agreed: 'Schváleno',
        intake: 'Přijato',
        repairing: 'V opravě',
        repaired: 'Opraveno',
        listed: 'Vystaveno',
        sold: 'Prodáno',
        awaiting_approval: 'Čeká na schválení',
        offer_sent: 'Nabídka odeslána',
        customer_deciding: 'Zákazník rozhoduje',
        accepted: 'Akceptováno',
        paid: 'Zaplaceno',
        stocked: 'Naskladněno',
        listed_for_sale: 'Vystaveno k prodeji',
        rejected: 'Zamítnuto',
        returned_to_seller: 'Vráceno prodejci',
        scrapped: 'Vyřazeno'
    };
    const classes = `inline-block px-2 py-0.5 rounded text-xs font-medium ${getBadgeStatusClass(status)}`;
    return <span className={classes}>{labelMap[status] || status || '-'}</span>;
};

function formatMoney(value, currency = 'CZK') {
    if (value === null || value === undefined || value === '') return '-';
    const number = Number(value);
    if (!Number.isFinite(number)) return '-';
    return `${number.toLocaleString('cs-CZ')} ${currency}`;
}

function getCustomerLabel(customer) {
    if (!customer) return '';
    if (customer.type === 'company') {
        return customer.company?.name || '';
    }
    const name = [customer.person?.name, customer.person?.surname].filter(Boolean).join(' ').trim();
    if (name) return name;
    return customer.emails?.[0]?.value || customer.phones?.[0]?.value || '';
}

function getIdentifierValues(identifiers) {
    if (Array.isArray(identifiers)) {
        return identifiers.map(item => item?.value).filter(Boolean);
    }
    if (identifiers && typeof identifiers === 'object') {
        return [identifiers.imei, identifiers.serialNumber, identifiers.serial, identifiers.sn].filter(Boolean);
    }
    return [];
}

function getLinkedRepair(row) {
    const repair = row?.links?.repairId;
    if (!repair) return null;
    if (typeof repair === 'string') {
        return {repairNumber: repair, status: ''};
    }
    return repair;
}

function getLinkedSale(row) {
    const sale = row?.links?.saleOrderId;
    if (!sale) return null;
    if (typeof sale === 'string') {
        return {saleNumber: sale, status: ''};
    }
    return sale;
}

function getRepairStatusLabel(status, t) {
    if (!status) return '-';
    const translated = t(`repairStatus.${status}`);
    return translated === `repairStatus.${status}` ? status : translated;
}

const resolveBuybackImageUrl = resolveBuybackPhotoUrl;

export default Buyback;
